import { HarvestYield } from '../../../types/HarvestYield';
import { HarvestYieldField } from '../../types/HarvestYieldsResponse';

/*
  We realized that the old endpoint (/api/rogue/v3/postharvestadjustedyield/)
  was returning incorrect data. So we are consuming an endpoint from OL,
  to avoid changes on the schema used on the app,
  this function transforms the new schema into the old schema.

  The old schema we should match is:
  [{
    "fieldUuid": string,
    "seasonTag": string,
    "nominalWeight": number,
    "yieldAverage": { "u": string, "q": number },
    "mass": {
      "wet": { "u": string, "q": number },
      "moisture": { "u": string, "q": number }
    },
    "area": {
      "harvest": { "u": string, "q": number }
    },
  }]

  While the new format coming from the OL endpoint is:
  {
    id: ID!
    seasons: {
      crop: Crop {
        id: ID!
        legacyIds: LegacyCropIds!
        name: String!
        nominalWeight: Measurement
      }
      harvest: {
        yield: Yield {
          wetMass: Measurement
          dryMass: Measurement
          moisture: Measurement
          area: Measurement!
          totalYield: Measurement!
          averageYield: Measurement!
        }
      }
      seasonTag: SeasonTag!
    }
  }
*/

/**
 *
 * @param {Object} field
 * @returns {Array}
 */
export function parseHarvestSeasons(field: HarvestYieldField | null): HarvestYield[] {
  if (!field) {
    return [];
  }

  return field.seasons.reduce((seasons: HarvestYield[], season) => {
    if (!season.harvest) {
      return seasons;
    }

    const dryYieldAverage = season.harvest.yield.averageDryYield ? {
      u: season.harvest.yield.averageDryYield.unit,
      q: season.harvest.yield.averageDryYield.value,
    } : undefined;

     const standardisedAverageYield = season.harvest.yield.standardisedAverageYield ? {
      u: season.harvest.yield.standardisedAverageYield.unit,
      q: season.harvest.yield.standardisedAverageYield.value,
    } : undefined;

    const seasonParsed: HarvestYield = {
      fieldUuid: field.id,
      seasonTag: season.seasonTag,
      nominalWeight: season.crop?.nominalWeight?.value,
      yieldAverage: {
        u: season.harvest.yield.averageYield.unit,
        q: season.harvest.yield.averageYield.value,
      },
      totalYield: {
        u: season.harvest.yield.totalYield.unit,
        q: season.harvest.yield.totalYield.value,
      },
      dryYieldAverage,
      standardisedAverageYield,
      mass: {
        wet: {
          u: season.harvest.yield.wetMass.unit,
          q: season.harvest.yield.wetMass.value,
        },
        dry: {
          u: season.harvest.yield.dryMass.unit,
          q: season.harvest.yield.dryMass.value,
        },
        moisture: {
          u: season.harvest.yield.moisture.unit,
          q: season.harvest.yield.moisture.value,
        },
        avgMoisture: {
          u: season.harvest.yield.moisture.unit,
          q: season.harvest.yield.moisture.value,
        },
      },
      area: {
        harvest: {
          u: season.harvest.yield.area.unit,
          q: season.harvest.yield.area.value,
        },
      },
    }

    return seasons.concat(seasonParsed);
  }, []);
}
